import type { SearchRequest as SearchItemsParams, SearchResponse as SearchResults } from "@/generated/api/types.gen"
import {
  ATTRIBUTE_PARAM_ALIASES,
  ATTRIBUTE_PARAM_PREFIX,
  DEFAULT_SEARCH_QUERY,
  LISTING_DEFAULT_QUERY,
  MAX_ITEMS_PER_PAGE,
  SEARCH_PARAM_KEYS,
  withAttribute,
} from "@/lib/types/search"
import type { SearchAttributes, SearchPageData, SearchQuery, SearchSort } from "@/lib/types/search"

export interface SearchItemsOptions {
  includeFacets?: boolean
}

interface URLSearchParamsLike {
  get(name: string): string | null
  has(name: string): boolean
  keys(): Iterable<string>
}

export type SearchParamsInput = { [key: string]: string | string[] | undefined } | URLSearchParamsLike

const SORT_TO_API_MAP: Record<SearchSort, string> = {
  price_asc: "price:asc",
  price_desc: "price:desc",
  relevance: "in_stock:desc,rank:asc",
} as const

const LISTING_PARAMS = new Set<string>(Object.values(SEARCH_PARAM_KEYS))

const ATTRIBUTES_BY_ALIAS = new Map(Object.entries(ATTRIBUTE_PARAM_ALIASES).map(([key, alias]) => [alias, key]))

const attributeParam = (key: string): string => ATTRIBUTE_PARAM_ALIASES[key] ?? `${ATTRIBUTE_PARAM_PREFIX}${key}`

const attributeOfParam = (param: string): string | undefined => {
  const aliased = ATTRIBUTES_BY_ALIAS.get(param)
  if (aliased) return aliased
  if (!param.startsWith(ATTRIBUTE_PARAM_PREFIX)) return undefined
  return param.slice(ATTRIBUTE_PARAM_PREFIX.length) || undefined
}

const selectedAttributes = (attributes: SearchAttributes = {}): [string, string[]][] => Object.entries(attributes).filter(([, values]) => values.length > 0)

export const queryToApiPayload = (query: SearchQuery, page: number, { includeFacets = false }: SearchItemsOptions = {}): SearchItemsParams => {
  return {
    searchString: query.text,
    searchTypes: ["MARKETPLACE"],
    categories: query.categories,
    collections: query.collections,
    attributes: Object.fromEntries(selectedAttributes(query.attributes)),
    pageNumber: page,
    itemsPerPage: query.limit,
    sortBy: SORT_TO_API_MAP[query.sort],
    minPrice: query.priceMin,
    maxPrice: query.priceMax,
    includeFacets,
    ...(query.inStockOnly === true ? { inStock: true } : {}),
  }
}

export const buildSearchPageData = (response: SearchResults, currentPage: number): SearchPageData => {
  return {
    products: response.result,
    suggestions: response.suggestions,
    pageNumber: currentPage,
    totalCount: response.pagination.totalCount,
    isEmpty: response.result.length === 0,
    hasMorePages: response.pagination.hasNextPage ?? false,
    facets: response.facets ?? undefined,
    serveId: response.serveId ?? undefined,
  }
}

export const queryToUrlParams = (query: SearchQuery, baseQuery: SearchQuery = DEFAULT_SEARCH_QUERY): URLSearchParams => {
  const params = new URLSearchParams()

  if (query.text) params.set(SEARCH_PARAM_KEYS.Q, query.text)
  if (query.sort !== DEFAULT_SEARCH_QUERY.sort) params.set(SEARCH_PARAM_KEYS.SORT, query.sort)
  if (query.priceMin !== undefined) params.set(SEARCH_PARAM_KEYS.MIN_PRICE, query.priceMin.toString())
  if (query.priceMax !== undefined) params.set(SEARCH_PARAM_KEYS.MAX_PRICE, query.priceMax.toString())
  if (query.inStockOnly !== undefined && query.inStockOnly !== baseQuery.inStockOnly) params.set(SEARCH_PARAM_KEYS.IN_STOCK, query.inStockOnly.toString())
  if (query.limit !== DEFAULT_SEARCH_QUERY.limit) params.set(SEARCH_PARAM_KEYS.PER_PAGE, query.limit.toString())
  if (query.categories.length) params.set(SEARCH_PARAM_KEYS.CATEGORIES, query.categories.join(","))
  if (query.collections.length) params.set(SEARCH_PARAM_KEYS.COLLECTIONS, query.collections.join(","))
  for (const [key, values] of selectedAttributes(query.attributes)) params.set(attributeParam(key), values.join(","))

  return params
}

export const listingBaseQuery = (baseQuery: Partial<SearchQuery> = {}): SearchQuery => ({
  ...DEFAULT_SEARCH_QUERY,
  ...LISTING_DEFAULT_QUERY,
  ...baseQuery,
})

export const listingQuery = (params: SearchParamsInput, baseQuery: Partial<SearchQuery> = {}): SearchQuery => urlParamsToQuery(params, listingBaseQuery(baseQuery))

function isURLSearchParamsLike(params: SearchParamsInput): params is URLSearchParamsLike {
  return "get" in params && "has" in params
}

export const urlParamsToQuery = (params: SearchParamsInput, baseQuery: SearchQuery = DEFAULT_SEARCH_QUERY): SearchQuery => {
  const getValue = (key: string): string | string[] | undefined => {
    if (isURLSearchParamsLike(params)) {
      return params.get(key) ?? undefined
    }
    return params[key]
  }
  const hasKey = (key: string): boolean => {
    if (isURLSearchParamsLike(params)) {
      return params.has(key)
    }
    return key in params && params[key] !== undefined
  }
  const paramNames = (): string[] => (isURLSearchParamsLike(params) ? [...params.keys()] : Object.keys(params))

  const getStringValue = (key: string): string | undefined => {
    const value = getValue(key)
    if (Array.isArray(value)) {
      return value[0]
    }
    return value
  }

  let sort: SearchSort = baseQuery.sort
  const sortParam = getStringValue(SEARCH_PARAM_KEYS.SORT)
  if (hasKey(SEARCH_PARAM_KEYS.SORT) && sortParam && isValidSearchSort(sortParam)) {
    sort = sortParam
  }

  const priceMin = hasKey(SEARCH_PARAM_KEYS.MIN_PRICE) ? parseNumber(getStringValue(SEARCH_PARAM_KEYS.MIN_PRICE)) : baseQuery.priceMin
  const priceMax = hasKey(SEARCH_PARAM_KEYS.MAX_PRICE) ? parseNumber(getStringValue(SEARCH_PARAM_KEYS.MAX_PRICE)) : baseQuery.priceMax
  const inStockOnly = parseBoolean(getStringValue(SEARCH_PARAM_KEYS.IN_STOCK)) ?? baseQuery.inStockOnly
  const limit = hasKey(SEARCH_PARAM_KEYS.PER_PAGE)
    ? Math.min(MAX_ITEMS_PER_PAGE, Math.max(1, parseNumber(getStringValue(SEARCH_PARAM_KEYS.PER_PAGE)) ?? baseQuery.limit))
    : baseQuery.limit
  const categories = hasKey(SEARCH_PARAM_KEYS.CATEGORIES) ? parseStringArray(getStringValue(SEARCH_PARAM_KEYS.CATEGORIES)) : baseQuery.categories
  const collections = hasKey(SEARCH_PARAM_KEYS.COLLECTIONS) ? parseStringArray(getStringValue(SEARCH_PARAM_KEYS.COLLECTIONS)) : baseQuery.collections
  const attributes = paramNames().reduce((selected, param) => {
    const key = attributeOfParam(param)
    return key && hasKey(param) ? withAttribute(selected, key, parseStringArray(getStringValue(param))) : selected
  }, baseQuery.attributes)

  return {
    text: hasKey(SEARCH_PARAM_KEYS.Q) ? (getStringValue(SEARCH_PARAM_KEYS.Q) ?? "") : baseQuery.text,
    categories,
    collections,
    attributes,
    priceMin,
    priceMax,
    inStockOnly,
    sort,
    limit,
  }
}

const parseNumber = (value: string | undefined): number | undefined => {
  if (value === undefined) return undefined
  const parsed = parseFloat(value)
  return Number.isNaN(parsed) ? undefined : parsed
}

const parseBoolean = (value: string | undefined): boolean | undefined => {
  if (value === "true") return true
  if (value === "false") return false
  return undefined
}

const parseStringArray = (value: string | undefined): string[] => {
  if (!value) return []
  return value.split(",").filter(Boolean)
}

const isValidSearchSort = (sort: string): sort is SearchSort => {
  return sort === "relevance" || sort === "price_asc" || sort === "price_desc"
}

// A feed-backed category/collection page can only serve its authored feed for the bare
// URL: the feed render never applies searchParams, so any listing-relevant param must
// fall through to the filterable listing or it is silently dropped.
export const hasListingParams = (params: Record<string, string | string[] | undefined>): boolean =>
  Object.entries(params).some(([param, value]) => {
    if (value === undefined || !(LISTING_PARAMS.has(param) || attributeOfParam(param))) return false
    return Array.isArray(value) ? value.some((entry) => entry !== "") : value !== ""
  })
